import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, Filter, RefreshCw, Building2, 
  CheckCircle, Clock, PlayCircle, Power,
  ChevronDown, X, Calendar, MapPin, Tag, Trash2, Zap,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight
} from 'lucide-react';
import HotelLifecycleCard from '../components/HotelLifecycleCard';
import { LIFECYCLE_STATUS, LIFECYCLE_STATUS_LABELS } from '../constants';
import { lifecycleAPI } from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import { userHasRole } from '../../../utils/roleHelpers';

const LifecycleDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Phase 13 — Start Offboarding modal state.
  const [showOffboardModal, setShowOffboardModal] = useState(false);
  const [offboardSelectedId, setOffboardSelectedId] = useState('');
  const [offboardSearch, setOffboardSearch] = useState('');
  
  const isAdmin = userHasRole(user, 'admin');
  // Phase 19.5 — Admin bulk-delete is now Admin-only by RBAC (the
  // localStorage `bulk_delete_enabled` flag is no longer required).
  const bulkDeleteEnabled = isAdmin;
  // Confirmation modal state for Bulk Delete (Phase 19.5).
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const handleSyncAll = async () => {
    if (!window.confirm('Sync all lifecycle records from Lead data? This will auto-complete steps based on existing Lead information.')) return;
    setSyncing(true);
    try {
      const res = await lifecycleAPI.syncAll();
      alert(`Sync complete: ${res.data.synced} synced, ${res.data.linked} newly linked, ${res.data.skipped} skipped`);
      loadHotels();
    } catch (error) {
      console.error('Error syncing:', error);
      alert('Failed to sync lifecycle records');
    } finally {
      setSyncing(false);
    }
  };
  
  // Advanced filters
  const [cityFilter, setCityFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [propertyStatusFilter, setPropertyStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Extract unique values for filter dropdowns
  const filterOptions = useMemo(() => {
    const cities = [...new Set(hotels.map(h => h.city).filter(Boolean))].sort();
    const states = [...new Set(hotels.map(h => h.state).filter(Boolean))].sort();
    const sources = [...new Set(hotels.map(h => h.source || h.leadSource).filter(Boolean))].sort();
    const propertyStatuses = [...new Set(hotels.map(h => h.propertyStatus).filter(Boolean))].sort();
    return { cities, states, sources, propertyStatuses };
  }, [hotels]);

  useEffect(() => {
    loadHotels();
  }, []);

  const loadHotels = async () => {
    try {
      setLoading(true);
      const response = await lifecycleAPI.getAll();
      setHotels(response.data || []);
      setSelectedIds([]);
    } catch (error) {
      console.error('Error loading hotels:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    // Phase 19.5 — open the proper confirmation modal instead of
    // window.confirm so the dialog text matches the product spec.
    setShowBulkDeleteModal(true);
  };

  const confirmBulkDelete = async () => {
    if (bulkDeleting || selectedIds.length === 0) return;
    setBulkDeleting(true);
    try {
      const res = await lifecycleAPI.bulkDelete(selectedIds);
      const result = res?.data || {};
      const failed = result.failed_ids || [];
      setSelectedIds([]);
      setShowBulkDeleteModal(false);
      await loadHotels();
      if (failed.length > 0) {
        alert(`${result.msg || 'Bulk delete completed'}\nFailed records: ${failed.join(', ')}`);
      } else {
        alert('Selected lifecycle records deleted successfully');
      }
    } catch (error) {
      console.error('Error bulk deleting:', error);
      const detail = error?.response?.data?.detail;
      alert(detail || 'Failed to delete records');
    } finally {
      setBulkDeleting(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === paginatedHotels.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(paginatedHotels.map(h => h.id));
    }
  };
  
  const clearAllFilters = () => {
    setCityFilter('');
    setStateFilter('');
    setSourceFilter('');
    setPropertyStatusFilter('');
    setDateFrom('');
    setDateTo('');
    setSearchQuery('');
    setStatusFilter('all');
  };

  const hasActiveFilters = cityFilter || stateFilter || sourceFilter || propertyStatusFilter || dateFrom || dateTo;

  // Helper to determine status from hotel data
  const getHotelStatus = (hotel) => {
    const propStatus = hotel.propertyStatus;
    const lcStatus = hotel.lifecycleStatus;
    
    // Phase 18b.3 — Offboarded (terminal state, 100% offboarding complete).
    // Detect via lifecycleStatus or propertyStatus, OR a 100% offboardingProgress
    // if the field is present. Inactive properties (auto-set after offboarding)
    // also surface here.
    const offboardingProgress = hotel.offboardingProgress;
    if (
      lcStatus === 'offboarded' ||
      propStatus === 'Offboarded' ||
      propStatus === 'Inactive' ||
      lcStatus === 'inactive' ||
      (typeof offboardingProgress === 'number' && offboardingProgress >= 100)
    ) {
      return LIFECYCLE_STATUS.OFFBOARDED;
    }
    
    // ONLY treat as ONBOARDED if finalGoLiveStatus is explicitly true
    if (hotel.finalGoLiveStatus) {
      return LIFECYCLE_STATUS.ONBOARDED;
    }
    
    // Offboarding states
    if (propStatus === 'Offboarding' || lcStatus === 'offboarding_in_progress' || lcStatus === 'offboarding') {
      return LIFECYCLE_STATUS.OFFBOARDING;
    }
    
    // Explicitly onboarded states (not default 'Active' property status)
    if (propStatus === 'Onboarded' || lcStatus === 'onboarded' || propStatus === 'Live' || lcStatus === 'live') {
      return LIFECYCLE_STATUS.ONBOARDED;
    }
    
    // Default to Onboarding for all other cases
    return LIFECYCLE_STATUS.ONBOARDING;
  };

  // Filter hotels
  const filteredHotels = hotels.filter(hotel => {
    const matchesSearch = !searchQuery || 
      hotel.propertyName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      hotel.hotelName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      hotel.city?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      hotel.state?.toLowerCase().includes(searchQuery.toLowerCase());
    
    let matchesStatus = statusFilter === 'all';
    if (!matchesStatus) {
      const hotelStatus = getHotelStatus(hotel);
      matchesStatus = hotelStatus === statusFilter;
    }
    
    // Advanced filters
    const matchesCity = !cityFilter || hotel.city === cityFilter;
    const matchesState = !stateFilter || hotel.state === stateFilter;
    const matchesSource = !sourceFilter || (hotel.source === sourceFilter || hotel.leadSource === sourceFilter);
    const matchesPropertyStatus = !propertyStatusFilter || hotel.propertyStatus === propertyStatusFilter;
    
    // Date range filter
    let matchesDateRange = true;
    if (dateFrom || dateTo) {
      const hotelDate = hotel.createdAt ? new Date(hotel.createdAt) : null;
      if (hotelDate) {
        if (dateFrom && new Date(dateFrom) > hotelDate) matchesDateRange = false;
        if (dateTo && new Date(dateTo) < hotelDate) matchesDateRange = false;
      }
    }
    
    return matchesSearch && matchesStatus && matchesCity && matchesState && matchesSource && matchesPropertyStatus && matchesDateRange;
  });

  // Pagination
  const totalFiltered = filteredHotels.length;
  const totalPages = itemsPerPage === 'all' ? 1 : Math.ceil(totalFiltered / itemsPerPage);
  const paginatedHotels = useMemo(() => {
    if (itemsPerPage === 'all') return filteredHotels;
    const start = (currentPage - 1) * itemsPerPage;
    return filteredHotels.slice(start, start + itemsPerPage);
  }, [filteredHotels, currentPage, itemsPerPage]);

  // Reset page when filters change
  useEffect(() => { setCurrentPage(1); }, [searchQuery, statusFilter, cityFilter, stateFilter, sourceFilter, propertyStatusFilter, dateFrom, dateTo, itemsPerPage]);

  // Calculate stats with new status values
  const stats = {
    total: hotels.length,
    onboarding: hotels.filter(h => getHotelStatus(h) === LIFECYCLE_STATUS.ONBOARDING).length,
    onboarded: hotels.filter(h => getHotelStatus(h) === LIFECYCLE_STATUS.ONBOARDED).length,
    offboarding: hotels.filter(h => getHotelStatus(h) === LIFECYCLE_STATUS.OFFBOARDING).length,
    offboarded: hotels.filter(h => getHotelStatus(h) === LIFECYCLE_STATUS.OFFBOARDED).length,
  };

  const StatCard = ({ label, value, icon: Icon, color, filterValue }) => (
    <button
      onClick={() => setStatusFilter(filterValue === statusFilter ? 'all' : filterValue)}
      className={`bg-white rounded-2xl border-2 p-5 transition-all duration-300 hover:shadow-lg ${
        statusFilter === filterValue 
          ? `border-${color}-400 shadow-md ring-2 ring-${color}-100` 
          : 'border-gray-100 hover:border-gray-200'
      }`}
      data-testid={`stat-card-${filterValue}`}
    >
      <div className="flex items-center gap-4">
        <div className={`w-14 h-14 rounded-xl bg-gradient-to-br from-${color}-100 to-${color}-50 flex items-center justify-center shadow-sm`}>
          <Icon size={28} className={`text-${color}-500`} />
        </div>
        <div className="text-left">
          <p className="text-3xl font-bold text-gray-800">{value}</p>
          <p className="text-sm text-gray-500 font-medium">{label}</p>
        </div>
      </div>
    </button>
  );

  return (
    <div className="space-y-6" data-testid="lifecycle-dashboard">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Hotel Lifecycle</h1>
            <p className="text-gray-500 mt-1">Manage onboarding and offboarding workflows</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Sync All Button */}
            {isAdmin && (
              <button
                onClick={handleSyncAll}
                disabled={syncing}
                className="flex items-center gap-2 px-4 py-2.5 bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-100 transition-colors font-medium"
                data-testid="sync-all-btn"
              >
                <Zap size={18} className={syncing ? 'animate-pulse' : ''} />
                {syncing ? 'Syncing...' : 'Sync All from Leads'}
              </button>
            )}
            {/* Bulk Delete Button */}
            {isAdmin && bulkDeleteEnabled && selectedIds.length > 0 && (
              <button
                onClick={handleBulkDelete}
                className="flex items-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-medium shadow-sm"
                data-testid="lifecycle-bulk-delete-btn"
              >
                <Trash2 size={18} />
                Delete Selected ({selectedIds.length})
              </button>
            )}
            {/* Start Offboarding Button — Phase 13: opens modal with fully-onboarded dropdown */}
            <button
              onClick={() => { setShowOffboardModal(true); setOffboardSelectedId(''); setOffboardSearch(''); }}
              className="flex items-center gap-2 px-5 py-2.5 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-colors font-medium shadow-sm"
              data-testid="start-offboarding-btn"
            >
              <Power size={18} />
              Start Offboarding
            </button>
            <button
              onClick={loadHotels}
              className="flex items-center gap-2 px-4 py-2.5 bg-teal-50 text-teal-600 rounded-xl hover:bg-teal-100 transition-colors font-medium"
              data-testid="refresh-btn"
            >
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards — Phase 18b.3 adds Offboarded (terminal) */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard 
          label="Total Hotels" 
          value={stats.total} 
          icon={Building2} 
          color="gray" 
          filterValue="all"
        />
        <StatCard 
          label="Onboarding" 
          value={stats.onboarding} 
          icon={PlayCircle} 
          color="blue" 
          filterValue={LIFECYCLE_STATUS.ONBOARDING}
        />
        <StatCard 
          label="Onboarded" 
          value={stats.onboarded} 
          icon={CheckCircle} 
          color="green" 
          filterValue={LIFECYCLE_STATUS.ONBOARDED}
        />
        <StatCard 
          label="Offboarding" 
          value={stats.offboarding} 
          icon={Clock} 
          color="orange" 
          filterValue={LIFECYCLE_STATUS.OFFBOARDING}
        />
        <StatCard 
          label="Offboarded" 
          value={stats.offboarded} 
          icon={CheckCircle} 
          color="slate" 
          filterValue={LIFECYCLE_STATUS.OFFBOARDED}
        />
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by hotel name, city, or state..."
              className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all"
              data-testid="search-input"
            />
          </div>

          {/* Status Filter Dropdown - Only Onboarding, Onboarded, Offboarding */}
          <div className="relative">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-5 py-3 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors min-w-[180px]"
              data-testid="filter-dropdown"
            >
              <Filter size={18} className="text-gray-500" />
              <span className="text-gray-700 font-medium">
                {statusFilter === 'all' ? 'All Status' : LIFECYCLE_STATUS_LABELS[statusFilter]}
              </span>
              <ChevronDown size={18} className={`text-gray-400 ml-auto transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>

            {showFilters && (
              <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-10">
                <button
                  onClick={() => { setStatusFilter('all'); setShowFilters(false); }}
                  className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 flex items-center gap-2 ${statusFilter === 'all' ? 'bg-teal-50 text-teal-700' : ''}`}
                >
                  <Building2 size={16} />
                  All Status
                </button>
                <button
                  onClick={() => { setStatusFilter(LIFECYCLE_STATUS.ONBOARDING); setShowFilters(false); }}
                  className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 flex items-center gap-2 ${statusFilter === LIFECYCLE_STATUS.ONBOARDING ? 'bg-teal-50 text-teal-700' : ''}`}
                >
                  <PlayCircle size={16} className="text-blue-500" />
                  Onboarding
                </button>
                <button
                  onClick={() => { setStatusFilter(LIFECYCLE_STATUS.ONBOARDED); setShowFilters(false); }}
                  className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 flex items-center gap-2 ${statusFilter === LIFECYCLE_STATUS.ONBOARDED ? 'bg-teal-50 text-teal-700' : ''}`}
                >
                  <CheckCircle size={16} className="text-green-500" />
                  Onboarded
                </button>
                <button
                  onClick={() => { setStatusFilter(LIFECYCLE_STATUS.OFFBOARDING); setShowFilters(false); }}
                  className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 flex items-center gap-2 ${statusFilter === LIFECYCLE_STATUS.OFFBOARDING ? 'bg-teal-50 text-teal-700' : ''}`}
                >
                  <Clock size={16} className="text-orange-500" />
                  Offboarding
                </button>
              </div>
            )}
          </div>

          {/* Advanced Filters Toggle */}
          <button
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className={`flex items-center gap-2 px-4 py-3 border rounded-xl transition-colors ${
              hasActiveFilters ? 'border-teal-500 bg-teal-50 text-teal-700' : 'border-gray-200 hover:bg-gray-50'
            }`}
            data-testid="advanced-filters-toggle"
          >
            <Filter size={18} />
            <span className="font-medium">Advanced</span>
            {hasActiveFilters && (
              <span className="bg-teal-500 text-white text-xs px-1.5 py-0.5 rounded-full">!</span>
            )}
          </button>
        </div>

        {/* Advanced Filters Panel */}
        {showAdvancedFilters && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-700">Advanced Filters</span>
              {hasActiveFilters && (
                <button
                  onClick={clearAllFilters}
                  className="text-sm text-red-500 hover:text-red-700 flex items-center gap-1"
                >
                  <X size={14} />
                  Clear All
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {/* City Filter */}
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  <MapPin size={12} className="inline mr-1" />
                  City
                </label>
                <select
                  value={cityFilter}
                  onChange={(e) => setCityFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none"
                  data-testid="city-filter"
                >
                  <option value="">All Cities</option>
                  {filterOptions.cities.map(city => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
              </div>

              {/* State Filter */}
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  <MapPin size={12} className="inline mr-1" />
                  State
                </label>
                <select
                  value={stateFilter}
                  onChange={(e) => setStateFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none"
                  data-testid="state-filter"
                >
                  <option value="">All States</option>
                  {filterOptions.states.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>

              {/* Lead Source Filter */}
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  <Tag size={12} className="inline mr-1" />
                  Lead Source
                </label>
                <select
                  value={sourceFilter}
                  onChange={(e) => setSourceFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none"
                  data-testid="source-filter"
                >
                  <option value="">All Sources</option>
                  {filterOptions.sources.map(source => (
                    <option key={source} value={source}>{source}</option>
                  ))}
                </select>
              </div>

              {/* Property Status Filter */}
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  <Building2 size={12} className="inline mr-1" />
                  Property Status
                </label>
                <select
                  value={propertyStatusFilter}
                  onChange={(e) => setPropertyStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none"
                  data-testid="property-status-filter"
                >
                  <option value="">All Status</option>
                  {filterOptions.propertyStatuses.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>

              {/* Date From */}
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  <Calendar size={12} className="inline mr-1" />
                  Date From
                </label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none"
                  data-testid="date-from-filter"
                />
              </div>

              {/* Date To */}
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  <Calendar size={12} className="inline mr-1" />
                  Date To
                </label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none"
                  data-testid="date-to-filter"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      <div className="flex justify-between items-center bg-white p-2.5 rounded-lg border border-gray-200 shadow-sm" data-testid="lifecycle-pagination-controls">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600 pl-2">Show</span>
          <select
            className="border border-gray-300 rounded-md text-sm p-1 focus:ring-teal-500 focus:border-teal-500 outline-none"
            value={itemsPerPage}
            onChange={(e) => { setItemsPerPage(e.target.value === 'all' ? 'all' : Number(e.target.value)); setCurrentPage(1); }}
            data-testid="lifecycle-items-per-page"
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={75}>75</option>
            <option value={100}>100</option>
            <option value="all">All</option>
          </select>
          <span className="text-sm text-gray-600">entries</span>
          <span className="text-xs text-gray-400 ml-2">({totalFiltered} total)</span>
        </div>
        {itemsPerPage !== 'all' && totalPages > 1 && (
          <div className="flex items-center gap-4">
            <div className="flex items-center border rounded-md overflow-hidden">
              <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="p-2 border-r hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="lifecycle-page-first">
                <ChevronsLeft size={16} />
              </button>
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-2 border-r hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="lifecycle-page-prev">
                <ChevronLeft size={16} />
              </button>
              <span className="px-4 py-1 text-sm font-medium text-gray-700 bg-gray-50" data-testid="lifecycle-page-info">
                Page {currentPage} of {totalPages}
              </span>
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-2 border-l hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="lifecycle-page-next">
                <ChevronRight size={16} />
              </button>
              <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="p-2 border-l hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="lifecycle-page-last">
                <ChevronsRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Hotels Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="bg-white rounded-2xl border border-gray-100 p-5 animate-pulse">
              <div className="h-1.5 bg-gray-200 rounded-full mb-4" />
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-gray-200 rounded-xl" />
                <div className="flex-1">
                  <div className="h-5 bg-gray-200 rounded w-3/4" />
                  <div className="h-4 bg-gray-100 rounded w-1/2 mt-2" />
                </div>
              </div>
              <div className="mt-4">
                <div className="h-8 bg-gray-100 rounded-lg" />
                <div className="h-2 bg-gray-200 rounded-full mt-3" />
              </div>
            </div>
          ))}
        </div>
      ) : filteredHotels.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-12 text-center">
          <div className="w-20 h-20 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Building2 size={40} className="text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            {searchQuery || statusFilter !== 'all' ? 'No hotels found' : 'No properties yet'}
          </h3>
          <p className="text-gray-500">
            {searchQuery || statusFilter !== 'all' 
              ? 'Try adjusting your search or filters' 
              : 'Properties from your CRM will appear here'}
          </p>
        </div>
      ) : (
        <div>
          {isAdmin && bulkDeleteEnabled && paginatedHotels.length > 0 && (
            <div className="flex items-center gap-3 mb-4 px-2">
              <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedIds.length === paginatedHotels.length && paginatedHotels.length > 0}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                  data-testid="lifecycle-select-all"
                />
                Select All ({selectedIds.length}/{paginatedHotels.length})
              </label>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {paginatedHotels.map(hotel => (
              <div key={hotel.id} className="relative">
                {isAdmin && bulkDeleteEnabled && (
                  <div className="absolute top-3 left-3 z-10">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(hotel.id)}
                      onChange={() => toggleSelect(hotel.id)}
                      className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                      data-testid={`lifecycle-select-${hotel.id}`}
                    />
                  </div>
                )}
                <HotelLifecycleCard hotel={hotel} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pagination Footer */}
      {!loading && totalFiltered > 0 && (
        <div className="px-4 py-2.5 bg-white rounded-lg border border-gray-200 text-xs text-gray-500 flex justify-between" data-testid="lifecycle-pagination-footer">
          <span>
            Showing {itemsPerPage === 'all' ? 1 : (currentPage - 1) * itemsPerPage + 1} to {itemsPerPage === 'all' ? totalFiltered : Math.min(currentPage * itemsPerPage, totalFiltered)} of {totalFiltered} entries
          </span>
          <span>Total Hotels: {hotels.length}</span>
        </div>
      )}

      {/* Phase 13 — Start Offboarding Modal: only fully-onboarded properties */}
      {showOffboardModal && (() => {
        const eligible = (hotels || []).filter(h => {
          const lc = (h.lifecycleStatus || '').toLowerCase();
          const ps = (h.propertyStatus || '').toLowerCase();
          const steps = h.onboardingSteps || {};
          const stepIds = ['agreement_documents','photos_listing_form','property_listing','quality_check','rates_channel_setup','post_live_review'];
          const allCompleted = stepIds.every(id => (steps[id]?.status || '').toLowerCase() === 'completed');
          // Onboarded property + every onboarding step actually completed.
          return (['onboarded','live','active'].includes(lc) || ['onboarded','live','active'].includes(ps)) && allCompleted;
        }).filter(h => {
          if (!offboardSearch.trim()) return true;
          const q = offboardSearch.toLowerCase();
          return (h.propertyName || h.hotelName || '').toLowerCase().includes(q)
              || (h.ownerName || '').toLowerCase().includes(q)
              || (h.city || '').toLowerCase().includes(q);
        });
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="offboard-modal">
            <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-2xl">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2"><Power size={18} className="text-orange-500"/> Start Offboarding</h2>
                  <p className="text-xs text-gray-500 mt-0.5">Only fully-onboarded properties are eligible.</p>
                </div>
                <button onClick={() => setShowOffboardModal(false)} className="p-1.5 hover:bg-gray-100 rounded-lg" data-testid="offboard-modal-close"><X size={18}/></button>
              </div>

              <input
                type="text"
                value={offboardSearch}
                onChange={(e) => setOffboardSearch(e.target.value)}
                placeholder="Search property name / owner / city…"
                className="w-full mb-3 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                data-testid="offboard-modal-search"
              />

              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Eligible Property</label>
              <select
                value={offboardSelectedId}
                onChange={(e) => setOffboardSelectedId(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-orange-500 outline-none"
                data-testid="offboard-modal-select"
              >
                <option value="">— Select a fully-onboarded property —</option>
                {eligible.map(h => (
                  <option key={h.id} value={h.id}>
                    {(h.propertyName || h.hotelName || 'Unnamed')} {h.city ? `· ${h.city}` : ''}
                  </option>
                ))}
              </select>
              {eligible.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">No properties have fully completed onboarding yet. Mark "Post Live Review" complete on a property first.</p>
              )}

              <div className="flex justify-end gap-2 mt-5">
                <button
                  onClick={() => setShowOffboardModal(false)}
                  className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  data-testid="offboard-modal-cancel"
                >Cancel</button>
                <button
                  disabled={!offboardSelectedId}
                  onClick={() => {
                    setShowOffboardModal(false);
                    // Phase 15.2 — robust deep-link route. `/hotel-lifecycle/offboarding/:id`
                    // is a real registered route in App.js, so the catch-all
                    // `*` → `/insights` redirect no longer eats this navigation.
                    // Workflow page reads :id via useParams() so refresh works too.
                    navigate(`/hotel-lifecycle/offboarding/${offboardSelectedId}`);
                  }}
                  className="px-4 py-2 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="offboard-modal-start"
                >Start Offboarding</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Phase 19.5 — Bulk Delete confirmation modal (Admin only). */}
      {showBulkDeleteModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => !bulkDeleting && setShowBulkDeleteModal(false)}
          data-testid="lifecycle-bulk-delete-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold text-gray-800 mb-2 flex items-center gap-2">
              <Trash2 size={20} className="text-red-600" /> Delete Lifecycle Records
            </h2>
            <p className="text-gray-600 text-sm">
              Are you sure you want to permanently delete the <strong>{selectedIds.length}</strong> selected lifecycle records?
            </p>
            <p className="text-rose-600 text-xs mt-2 font-medium">This action cannot be undone.</p>
            <div className="flex gap-3 pt-5">
              <button
                type="button"
                disabled={bulkDeleting}
                onClick={() => setShowBulkDeleteModal(false)}
                className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 font-medium"
                data-testid="lifecycle-bulk-delete-cancel"
              >Cancel</button>
              <button
                type="button"
                disabled={bulkDeleting}
                onClick={confirmBulkDelete}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="lifecycle-bulk-delete-confirm"
              >
                {bulkDeleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LifecycleDashboard;
